/**
 * Sprint B — W4.
 *
 * Walk a section's prose looking for numeric tokens that map back to
 * fact-sheet entries. A token ``"600,0 m²"`` next to a fact_sheet
 * value ``cestica_povrsina_m2 = 600`` is highlighted with a
 * shadcn Tooltip surfacing the originating fact key + (when IFC-
 * derived) the GlobalIds.
 *
 * Token grammar — kept conservative to avoid false positives:
 *
 * * Croatian decimal — comma separator (``600,0``).
 * * English decimal — dot separator (``600.0``).
 * * Thousands separator — narrow-NBSP, regular space, or dot
 *   (``1 234,56`` / ``1.234,56``). Mixed grouping is rejected.
 * * Optional unit suffix — ``m²``, ``m2``, ``m``, ``cm``, ``%``.
 * * Multi-token strings like ``Po+P+2`` are NOT matched (no leading
 *   digits across the ``+``).
 *
 * Only tokens whose parsed value numerically matches at least one
 * fact-sheet leaf are emitted — silent on the rest so the architect
 * isn't drowning in tooltips on incidental numbers.
 */

export interface NumericMatch {
  /** Inclusive start index in the source string. */
  startIdx: number;
  /** Exclusive end index in the source string. */
  endIdx: number;
  /** Verbatim slice from the source (not re-formatted). */
  text: string;
  /** Parsed numeric value (Croatian/English locales normalised). */
  value: number;
  /** Detected unit suffix or ``null``. */
  unit: string | null;
  /** Fact-sheet keys whose value matches ``value``. Always non-empty. */
  candidateFactKeys: string[];
}

interface FactLeaf {
  key: string;
  value: number;
}

const NUMBER_RE =
  // Two alternatives, ordered (JS regex picks the first that matches at a
  // given position):
  //   1. Grouped — at least one thousands separator group, e.g. ``1 234,56``
  //      or ``1.234,56``. We require ``+`` (one or more groups) so this
  //      alternative never wins on plain integers — that prevents the
  //      English-decimal case ``7.5`` from matching only the ``7`` here.
  //   2. Plain — ``600``, ``600,0``, ``7.5`` — single dot or comma decimal.
  // Allowed grouping separators: regular space, NBSP (\u00A0),
  // narrow NBSP (\u202F), dot.
  /(\d{1,3}(?:[ \u00A0\u202F.]\d{3})+(?:,\d+)?|\d+(?:[.,]\d+)?)/g;

// Unit suffix lookahead. We use ``(?!\w)`` instead of ``\b`` because the
// natural boundary after ``m²`` / ``%`` / ``€`` is non-word-on-both-sides
// (``²``, ``%``, ``€`` are themselves not ``\w``), which would defeat
// ``\b`` and leave the unit truncated to ``m``.
const UNIT_SUFFIX_RE = /^(?:\s)*(m²|m2|m|cm|mm|km|%|kn|€)(?!\w)/i;

export function parseNumericTokens(
  content: string,
  factSheet: Record<string, unknown> | null | undefined,
): NumericMatch[] {
  if (!content || !factSheet) return [];

  const leaves = collectFactLeaves(factSheet);
  if (leaves.length === 0) return [];

  const matches: NumericMatch[] = [];
  for (const m of content.matchAll(NUMBER_RE)) {
    const text = m[0];
    const startIdx = m.index ?? 0;
    if (startIdx == null) continue;
    const value = parseLocaleNumber(text);
    if (!Number.isFinite(value)) continue;

    // Detect optional unit immediately after the token. Unit length is
    // counted into the highlight span so the tooltip target reads
    // naturally ("600,0 m²" instead of just "600,0 ").
    let endIdx = startIdx + text.length;
    let unit: string | null = null;
    const tail = content.slice(endIdx);
    const unitMatch = tail.match(UNIT_SUFFIX_RE);
    if (unitMatch) {
      unit = unitMatch[1].toLowerCase();
      endIdx += unitMatch[0].length;
    }

    const candidates = findCandidates(value, leaves);
    if (candidates.length === 0) continue;
    matches.push({
      startIdx,
      endIdx,
      text: content.slice(startIdx, endIdx),
      value,
      unit,
      candidateFactKeys: candidates,
    });
  }

  return dedupeOverlapping(matches);
}

function parseLocaleNumber(raw: string): number {
  // Strategy:
  //   1. Strip thousands separators (space variants + dot when comma exists).
  //   2. Replace decimal comma with dot.
  //   3. Standard parseFloat.
  const hasComma = raw.includes(",");
  let stripped = raw.replace(/[\u00A0\u202F ]/g, "");
  if (hasComma) {
    stripped = stripped.replace(/\./g, "").replace(",", ".");
  }
  return parseFloat(stripped);
}

function collectFactLeaves(
  factSheet: Record<string, unknown>,
): FactLeaf[] {
  const out: FactLeaf[] = [];
  walk(factSheet, "", out);
  return out;
}

function walk(node: unknown, path: string, out: FactLeaf[]): void {
  if (node === null || node === undefined) return;
  if (typeof node === "number" && Number.isFinite(node)) {
    if (path) out.push({ key: lastSegment(path), value: node });
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node) walk(item, path, out);
    return;
  }
  if (typeof node === "object") {
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      // Internal facts (``_sources`` etc.) are bookkeeping, not figures
      // that the LLM is allowed to cite — skip them so we don't suggest
      // them as candidates.
      if (k.startsWith("_")) continue;
      walk(v, path ? `${path}.${k}` : k, out);
    }
  }
}

function lastSegment(path: string): string {
  const idx = path.lastIndexOf(".");
  return idx === -1 ? path : path.slice(idx + 1);
}

const MATCH_TOLERANCE = 0.01;

function findCandidates(value: number, leaves: FactLeaf[]): string[] {
  // Tolerance for floating-point noise + cm/m round-tripping. Anything
  // larger would cause spurious matches between e.g. ``5`` and ``5.5``.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const leaf of leaves) {
    const diff = Math.abs(leaf.value - value);
    if (diff > MATCH_TOLERANCE && diff / Math.max(Math.abs(leaf.value), 1) > 0.001) {
      continue;
    }
    if (seen.has(leaf.key)) continue;
    seen.add(leaf.key);
    out.push(leaf.key);
  }
  return out;
}

function dedupeOverlapping(matches: NumericMatch[]): NumericMatch[] {
  // ``\d{1,3}(?:[ .]\d{3})*`` may, in theory, split into overlapping
  // sub-tokens on weird input — guard against rendering broken span
  // trees by keeping the longest match starting at each position.
  matches.sort((a, b) => a.startIdx - b.startIdx || b.endIdx - a.endIdx);
  const out: NumericMatch[] = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.startIdx < lastEnd) continue;
    out.push(m);
    lastEnd = m.endIdx;
  }
  return out;
}
